import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, CheckCircle, X, ArrowLeft } from "lucide-react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";


export default function ResetPassword() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");
  const otp = queryParams.get("otp");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { resetPasswordAPI, loading: isLoading } = useAdminAuth();

  // Password strength calculations
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const allRequirementsMet = hasMinLength && hasUpperCase && hasDigit;
  const passwordsMatch =
    password && confirmPassword && password === confirmPassword;

  // Calculate password strength
  const getPasswordStrength = () => {
    if (!password) return { label: "", color: "", width: "0%" };
    let strength = 0;
    if (hasMinLength) strength++;
    if (hasUpperCase) strength++;
    if (hasDigit) strength++;
    if (password.length >= 12) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2)
      return { label: "Weak", color: "bg-red-500", width: "33%" };
    if (strength <= 3)
      return { label: "Medium", color: "bg-[#8B5E3C]/40", width: "66%" };
    return { label: "Strong", color: "bg-[#8B5E3C]", width: "100%" };
  };

  const strength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      showNotification({
        type: "failed",
        title: "Weak Password",
        message: "Please meet all password requirements."
      });
      return;
    }

    if (!passwordsMatch) {
      showNotification({
        type: "failed",
        title: "Mismatch",
        message: "Passwords do not match."
      });
      return;
    }

    const res = await resetPasswordAPI(email, otp, password);
    if (res && res.status === "success") {
      setTimeout(() => {
        navigate("/admin/login");
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-2 text-[#8B5E3C] hover:text-[#111111] mb-8 font-inter font-bold uppercase tracking-wider text-xs transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>

        {/* Logo and Brand */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="w-20 h-20 bg-white border border-[#E5E7EB] rounded-2xl flex items-center justify-center shadow-sm mx-auto mb-6 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
            <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun" className="w-full h-full object-contain p-2" loading="eager" />
          </div>
          <h1 className="text-3xl font-normal text-[#111111] dark:text-white mb-2" style={{ fontFamily: "Playfair Display, Georgia, serif" }}>
            Reset Password
          </h1>
          <p className="text-black dark:text-[#A0A0A0] font-inter uppercase tracking-[2px] text-xs font-semibold">
            Create a new secure password
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-[#F0F0F0] dark:border-[#2A2A2A] p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">

            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                New Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black dark:text-[#A0A0A0]"
                  size={16}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-black/60 dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black hover:text-[#8B5E3C] dark:text-[#A0A0A0] transition-colors duration-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {password && (
              <div className="space-y-3 animate-fade-in bg-[#FAFAFA] p-4 rounded-xl border border-[#F0F0F0]">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#999999] font-inter">
                    Strength:
                  </span>
                  <span
                    className={`text-xs font-bold uppercase tracking-wider ${strength.color.replace("bg-", "text-")} font-inter`}
                  >
                    {strength.label}
                  </span>
                </div>
                <div className="w-full h-1 bg-[#E5E7EB] dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strength.color} transition-all duration-300`}
                    style={{ width: strength.width }}
                  ></div>
                </div>
                <div className="space-y-2 pt-2">
                  <div
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${hasMinLength ? "text-green-600" : "text-[#CCCCCC]"} font-inter`}
                  >
                    {hasMinLength ? <CheckCircle size={12} /> : <X size={12} />}
                    <span>8+ characters</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${hasUpperCase ? "text-green-600" : "text-[#CCCCCC]"} font-inter`}
                  >
                    {hasUpperCase ? <CheckCircle size={12} /> : <X size={12} />}
                    <span>Uppercase</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${hasDigit ? "text-green-600" : "text-[#CCCCCC]"} font-inter`}
                  >
                    {hasDigit ? <CheckCircle size={12} /> : <X size={12} />}
                    <span>Number</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                Confirm Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#999999] dark:text-[#A0A0A0]"
                  size={16}
                />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-black/60 dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                  placeholder="Confirm new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black hover:text-[#8B5E3C] dark:text-[#A0A0A0] transition-colors duration-200"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              {confirmPassword && (
                <p
                  className={`text-xs font-bold uppercase tracking-wider font-inter ${passwordsMatch ? "text-green-600" : "text-red-600"}`}
                >
                  {passwordsMatch
                    ? "✓ Match"
                    : "✗ No match"}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !allRequirementsMet || !passwordsMatch}
              className="w-full py-4 px-6 bg-[#111111] hover:bg-[#8B5E3C] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 font-inter uppercase tracking-[2px] text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-black dark:text-[#666666] mt-10 font-inter uppercase tracking-widest">
          &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
        </p>
      </div>

      <style jsx global>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
}
